import { makeArticle } from "./learnFactory";

export const pageAnxietyChestTightness = makeArticle({
  slug: "anxiety-chest-tightness-when-to-worry",
  title: "Anxiety Chest Tightness: When to Worry | Neuro Wellness",
  description:
    "Anxiety can cause chest tightness, but some chest pain is an emergency. Learn the warning signs, why anxiety tightens the chest, and how to calm it.",
  canonical: "/learn/anxiety-chest-tightness-when-to-worry",
  h1: "Anxiety Chest Tightness: Why It Happens and When to Worry",
  breadcrumbLabel: "Anxiety Chest Tightness",
  shortAnswer:
    "Anxiety can cause real chest tightness, pressure or sharp chest pain. But chest pain can also be a heart attack, and the two can feel alike. If chest pain is new or severe, spreads to your arm, jaw, neck or back, or comes with shortness of breath, a cold sweat, nausea or lightheadedness, call 911 now. Once a medical provider has checked your heart, recurring chest tightness during stress is often anxiety, and it responds well to treatment.",
  keyTakeaways: [
    "Never assume chest pain is anxiety until a medical provider has checked your heart.",
    "Anxiety tightens chest muscles and speeds breathing and heart rate, which can cause pressure or pain.",
    "Slow breathing with a longer exhale can calm anxiety chest tightness within minutes.",
    "If it keeps coming back, treating the anxiety itself is what stops it.",
  ],
  sections: [
    {
      heading: "When to get help now: call 911",
      paragraphs: [
        "Call 911 or go to the nearest emergency room if chest pain or pressure:",
      ],
      list: [
        "**Is new, severe or crushing.** Or feels like pressure, squeezing or fullness that lasts more than a few minutes or comes and goes.",
        "**Spreads.** To one or both arms, the shoulder, jaw, neck, back or stomach.",
        "**Comes with other symptoms.** Shortness of breath, a cold sweat, nausea, lightheadedness, fainting, or a rapid or irregular heartbeat.",
        "**Starts with exertion.** Or doesn't ease with rest.",
      ],
      paragraphsAfter: [
        "Women, older adults and people with diabetes are more likely to have heart attack symptoms that feel unusual or mild. When in doubt, get checked. Being told “it was anxiety” is a good outcome, not a wasted trip.",
      ],
    },
    {
      heading: "The science: why anxiety tightens the chest",
      paragraphs: [
        "When your brain senses danger, the fight-or-flight response kicks in:",
      ],
      list: [
        "**Muscle tension.** The muscles between your ribs and across your chest tighten, which can feel like a band or pressure.",
        "**Fast, shallow breathing.** Breathing from the upper chest strains chest muscles and can cause tingling, dizziness and the feeling you can't get a full breath.",
        "**Racing heart.** Adrenaline speeds your heart rate and makes your heartbeat feel strong or pounding.",
        "**Heightened attention.** Once you notice a chest sensation, worry about it adds more adrenaline, which makes the sensation stronger. This loop is why anxiety chest pain can escalate quickly.",
      ],
    },
    {
      heading: "What anxiety chest tightness often feels like",
      paragraphs: [],
      list: [
        "Tightness, pressure or a squeezing band across the chest",
        "Sharp, stabbing pain in one spot that comes and goes",
        "Soreness in the chest wall, sometimes tender to touch",
        "A feeling of not getting a full breath",
        "A pounding or racing heartbeat",
      ],
      paragraphsAfter: [
        "According to the National Institute of Mental Health, chest pain and a pounding heart are common panic attack symptoms, and an attack can last from a few minutes to an hour. These patterns are clues, not a diagnosis. Only a medical evaluation can rule out heart, lung and other causes.",
      ],
    },
    {
      heading:
        "5 ways to calm anxiety chest tightness (once your heart has been checked)",
      paragraphs: [],
      list: [
        "**Slow your exhale.** Breathe in through your nose for 4 seconds and out through your mouth for 6 to 8 seconds. A longer exhale signals your nervous system to settle.",
        "**Breathe low.** Put one hand on your chest and one on your belly. Aim to move the lower hand, not the upper one.",
        "**Release the chest and shoulders.** Drop your shoulders, unclench your jaw and gently roll your shoulders back several times.",
        "**Ground yourself.** Name 5 things you can see, 4 you can hear and 3 you can touch to pull attention out of the chest-sensation loop.",
        "**Let the wave pass.** Remind yourself: “I've been checked. This is anxiety. It will peak and pass.” Fighting the sensation often feeds it.",
      ],
    },
    {
      heading: "Treating the root cause",
      paragraphs: [
        "If chest tightness keeps coming back, the goal is to treat the anxiety, not just survive each episode. Cognitive behavioral therapy, especially for panic, is highly effective. Medication can help. Regular exercise, steady sleep and less caffeine lower the baseline.",
        "Anxiety and depression often travel together. Neuro Wellness TMS Centers of America in Coral Springs, Florida offers [[anxiety evaluation and treatment|/services/anxiety]], [[psychotherapy|/services/psychotherapy]] and [[medication management|/services/medication-management]]. When anxiety comes with major depression that hasn't improved on antidepressants, we are also a [[Verified SAINT® Provider|/saint]]. Learn more about being [[depressed and anxious at the same time|/depressed-and-anxious-at-the-same-time]].",
      ],
    },
    {
      heading: "What to do next",
      paragraphs: [
        "If your heart has been checked and chest tightness keeps returning with stress, it's time to treat the anxiety itself.",
        "Our team offers a free consultation to talk through your options. Call 954-827-9793.",
      ],
      links: [
        { to: "/services/anxiety", text: "Get relief from anxiety" },
        { to: "/contactus", text: "Schedule a free consultation" },
      ],
    },
  ],
  faqs: [
    {
      question: "Can anxiety cause chest tightness?",
      answer:
        "Yes. Anxiety tenses the chest-wall muscles and speeds up breathing and heart rate, which can cause tightness, pressure or sharp pain. Because heart problems can feel similar, new or severe chest pain should always be checked first.",
    },
    {
      question: "How do I know if chest pain is anxiety or a heart attack?",
      answer:
        "You can't reliably tell on your own. Chest pain that spreads to the arm, jaw or back, or comes with shortness of breath, sweating, nausea or lightheadedness, needs emergency care, so call 911.",
    },
    {
      question: "How long does anxiety chest tightness last?",
      answer:
        "A panic attack can last from a few minutes to an hour, and muscle tension from ongoing anxiety can linger longer. Chest pain that lasts more than a few minutes or keeps returning should be evaluated by a medical provider.",
    },
    {
      question: "What helps anxiety chest pain fast?",
      answer:
        "Slow breathing with a longer exhale, breathing into your belly, relaxing your shoulders and grounding your attention can calm it within minutes. If you haven't been medically checked, get evaluated first.",
    },
    {
      question: "Can anxiety chest tightness last for days?",
      answer:
        "Ongoing anxiety can keep chest muscles tense and sore for days. Persistent chest pain still needs a medical evaluation to rule out other causes.",
    },
  ],
  sources: [
    {
      citation: "American Heart Association. Warning Signs of a Heart Attack.",
      url: "https://www.heart.org/en/health-topics/heart-attack/warning-signs-of-a-heart-attack",
    },
    {
      citation:
        "National Institute of Mental Health. Panic Disorder: When Fear Overwhelms.",
      url: "https://www.nimh.nih.gov/health/publications/panic-disorder-when-fear-overwhelms",
    },
    {
      citation: "National Institute of Mental Health. Anxiety Disorders.",
      url: "https://www.nimh.nih.gov/health/topics/anxiety-disorders",
    },
  ],
  relatedLinks: [
    {
      title: "Anxiety treatment",
      href: "/services/anxiety",
      description: "How we evaluate and treat anxiety in Coral Springs.",
    },
    {
      title: "Panic, anxiety and depression",
      href: "/panic-anxiety-depression",
      description: "When panic attacks and low mood overlap.",
    },
    {
      title: "Can't stop crying for no reason?",
      href: "/learn/cant-stop-crying-for-no-reason",
      description: "What frequent crying can mean and when to get help.",
    },
    {
      title: "Anxiety nausea",
      href: "/anxiety-nausea-why-it-happens-how-to-stop-it",
      description: "Why anxiety upsets your stomach and how to stop it.",
    },
    {
      title: "Why is my anxiety worse in the morning?",
      href: "/learn/why-is-my-anxiety-worse-in-the-morning",
      description: "Morning cortisol, sleep and simple fixes.",
    },
  ],
});
